import React, { useMemo, useState } from "react";
import styles from "./style.module.scss";
import BusinessInformation from "./components/business-information";
import PersonalInformation from "./components/personal-information";
import BusinessProfile from "./components/business-profile";
import CreditCardProcessing from "./components/credit-card-processing";
import ConditionalAddendums from "./components/conditional-addendums";
import Pricing from "./components/pricing";
import Equipment from "./components/equipment";
import Details from "./components/details";
import Processor from "./components/processor";
import TravelAddendumReview from "./components/travel-addendum";
import SignAgreement from "./components/sign-agreement";
import Alert from "../../../../../../components/alert";
import useSalesFormStore from "../../../../../../stores/sales-form";
import useUserStore from "../../../../../../stores/user";
import { withRouter } from "react-router-dom";
import { conditionalAddendums as addendumNames } from "../../../../../../../constants";
import { isDisputeRecoveryVisible } from "../../../../../../utils/is-dispute-recovery-visible";

/**
 * ReviewSubmit Component
 * Renders a review page showing all sections of the application form
 * with the ability to toggle sections and edit individual parts
 *
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance containing values, errors, and handlers
 * @param {Function} props.onSectionChange - Callback function when a section edit button is clicked
 */
const ReviewSubmit = ({
  onSectionChange,
  pricingModel,
  pricingData,
  equipmentData,
  activeAppData,
  reRender,
  history,
  incompleteStep = null,
  stepCompletion = {},
}) => {
  const [openSections, setOpenSections] = useState({
    "business-information": true,
    "personal-information": false,
    "business-profile": false,
    "credit-card-processing": false,
    "conditional-addendums": false,
    "travel-addendum": false,
    pricing: false,
    equipment: false,
    processor: false,
    details: false,
  });

  const isGodUser = useUserStore((state) => state.isGodUser);

  const { cbdRequired, vapeRequired, travelRequired } = useMemo(() => {
    if (!isGodUser) {
      return { cbdRequired: false, vapeRequired: false, travelRequired: false };
    }
    const overrideBank = activeAppData?.override_bank || [];
    const overrideAddendums = activeAppData?.override_addendums || [];
    const hasKurvBank = overrideBank.some((bank) => bank?.value === "Kurv");
    return {
      cbdRequired: hasKurvBank && overrideAddendums.includes(addendumNames.CBD_ADDENDUM),
      vapeRequired: hasKurvBank && overrideAddendums.includes(addendumNames.VAPE_ATTESTATION),
      travelRequired: hasKurvBank && overrideAddendums.includes(addendumNames.TRAVEL_ADDENDUM),
    };
  }, [isGodUser, activeAppData?.override_bank, activeAppData?.override_addendums]);

  const isAuthenticated = useSalesFormStore((state) => state.isAuthenticated);
  const otpVerificationTime = useSalesFormStore(
    (state) => state.otpVerificationTime,
  );

  const user = useUserStore((state) => state.user);

  const disputeRecoveryVisible = isDisputeRecoveryVisible(
    activeAppData?.organization_id ?? user?.organization_id,
  );

  /**
   * Toggles the visibility of a section
   * @param {string} section - Section identifier to toggle
   */
  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const onEditPricingAndEquipment = () => {
    if (user) {
      history.push(`/v2/edit-pricing-equipment/${activeAppData.editslug}`);
    }
  };

  return (
    <div className={styles.reviewSubmit}>      
      <BusinessInformation
        onEdit={activeAppData?.lead_progression_id < 7  ? () => onSectionChange(0) : false}
        isOpen={openSections["business-information"]}
        onToggle={() => toggleSection("business-information")}
        complete={stepCompletion[0]}
      />
      <PersonalInformation
        onEdit={activeAppData?.lead_progression_id < 7 ? () => onSectionChange(1) : false}
        isOpen={openSections["personal-information"]}
        onToggle={() => toggleSection("personal-information")}
        complete={stepCompletion[1]}
      />
      <BusinessProfile
        onEdit={activeAppData?.lead_progression_id < 7 ? () => onSectionChange(2) : false}
        isOpen={openSections["business-profile"]}
        onToggle={() => toggleSection("business-profile")}
        disputeRecoveryVisible={disputeRecoveryVisible}
        complete={stepCompletion[2]}
      />
      <CreditCardProcessing
        onEdit={activeAppData?.lead_progression_id < 7 ? () => onSectionChange(3) : false}
        isOpen={openSections["credit-card-processing"]}
        onToggle={() => toggleSection("credit-card-processing")}
        complete={stepCompletion[3]}
      />
      <ConditionalAddendums
        cbdRequired={cbdRequired}
        vapeRequired={vapeRequired}
        isOpen={openSections["conditional-addendums"]}
        onToggle={() => toggleSection("conditional-addendums")}
        onEdit={activeAppData?.lead_progression_id < 7 ? () => onSectionChange(3) : false}
      />
      {travelRequired && (
        <TravelAddendumReview
          isOpen={openSections["travel-addendum"]}
          onToggle={() => toggleSection("travel-addendum")}
          onEdit={activeAppData?.lead_progression_id < 7 ? () => onSectionChange(3) : false}
        />
      )}
      <Pricing
        isOpen={openSections["pricing"]}
        onToggle={() => toggleSection("pricing")}
        onEdit={user && activeAppData?.lead_progression_id < 7 ? onEditPricingAndEquipment : false}
      />
      <Equipment
        isOpen={openSections["equipment"]}
        onToggle={() => toggleSection("equipment")}
        appData={activeAppData}
        onEdit={user && activeAppData?.lead_progression_id < 7 ? onEditPricingAndEquipment : false}
      />
      <Details
        onEdit={activeAppData?.lead_progression_id < 7 ? () => onSectionChange("details") : false}
        isOpen={openSections["details"]}
        onToggle={() => toggleSection("details")}
        appData={activeAppData}
      />
      {activeAppData?.user?.is_pc_admin && (
        <Processor
          onEdit={() => onSectionChange("processor")}
          isOpen={openSections["processor"]}
          onToggle={() => toggleSection("processor")}
        />
      )}

      {/* An agent can send an incomplete app for signature, and the merchant can
          land here from the stepper. They finish the gaps before signing. */}
      {isAuthenticated && incompleteStep !== null && (
        <Alert type="warning" fullWidth>
          <span className={styles.incompleteNotice}>
            Some required information is still missing.{" "}
            <button
              type="button"
              onClick={() => onSectionChange(incompleteStep)}
            >
              Complete it
            </button>
          </span>
        </Alert>
      )}

      {isAuthenticated && otpVerificationTime && incompleteStep === null && (
        <SignAgreement reRender={reRender} />
      )}
    </div>
  );
};

export default withRouter(ReviewSubmit);
